import React from 'react';
import { InformationCardProps } from '../../InformationCard.types';
import { Badge } from '../../../badge';
import { styledComponent } from '../../../../hoc';

/**
 * `BadgeContainer` is a styled `div` element that arranges badges
 * in a responsive grid-like format with consistent spacing.
 *
 * @remarks
 * - Uses `flex` layout with `columnGap` and `rowGap` for spacing.
 * - Wraps child elements when space is insufficient.
 */
const BadgeContainer = styledComponent('div')(() => ({
  display: 'inline-flex',
  flexDirection: 'column',
  padding: 0,
  flexWrap: 'wrap',
  columnGap: 12,
  rowGap: 12,
  marginRight: 12,
}));

/**
 * `CardBadge` Component
 *
 * Renders a list of `Badge` components based on the provided `items` in `props.data`.
 *
 * @throws Will throw an error if `props.data.items` is not provided.
 *
 * @param {InformationCardProps} props - Props containing the badge data.
 * @returns {JSX.Element} A styled container displaying a list of badges.
 *
 * @example
 * ```tsx
 * import CardBadge from './CardBadge';
 *
 * const data = {
 *   items: [
 *     {
 *       primary: 'Badge Name',
 *       image: 'https://example.com/image.png',
 *       secondary: 'Badge Description',
 *       metadata: {
 *         dateAwarded: '2024-06-17',
 *         version: 'v1.0',
 *       },
 *     },
 *   ],
 * };
 *
 * export const App = () => <CardBadge data={data} />;
 * ```
 *
 * @category InformationCard
 * @document CardBadgeComponentUsage.md
 */
export const CardBadge = (props: InformationCardProps): JSX.Element => {
  // Check if items exist in the data; if not, throw an error
  if (props.data?.items) {
    return (
      <BadgeContainer>
        {props.data.items.map((item, index) => {
          return (
            <Badge
              key={`${item.primary}-${index}_badge`}
              name={item.primary}
              img={item.image}
              description={item.secondary ?? ''}
              dateAwarded={item.metadata?.dateAwarded ?? ''}
              version={item.metadata?.version ?? ''}
            />
          );
        })}
      </BadgeContainer>
    );
  } else {
    throw new Error('CardBadge component must have items');
  }
};
